/**
 * Generate a record ID in the format: GW-0001-16-11-2025-1624-00003
 * Format: INITIALS-GROUPCODE-DD-MM-YYYY-HHMM-SEQUENCE
 */

interface User {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

interface Group {
  id: string;
  code?: string;
  name: string;
}

function getUserInitials(user: User): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }

  // Fallback to email
  const emailParts = user.email.split('@')[0];
  if (emailParts.length >= 2) {
    return emailParts.substring(0, 2).toUpperCase();
  }

  return 'XX';
}

function getGroupCode(group: Group): string {
  if (group.code) {
    return group.code.padStart(4, '0');
  }

  // Generate code from group name or ID
  const nameCode = group.name.substring(0, 4).toUpperCase().padEnd(4, 'X');
  return nameCode;
}

async function getNextSequenceNumber(_userId: string): Promise<number> {
  // Get count of records created by this user
  // For now, use timestamp-based sequence
  const timestamp = Date.now();
  const sequence = (timestamp % 100000);
  return sequence;
}

export async function generateRecordId(
  user: User,
  group: Group,
  _recordType: 'snare' | 'carcass'
): Promise<string> {
  const initials = getUserInitials(user);
  const groupCode = getGroupCode(group);

  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  const sequence = await getNextSequenceNumber(user.id);
  const sequenceStr = String(sequence).padStart(5, '0');

  return `${initials}-${groupCode}-${day}-${month}-${year}-${hours}${minutes}-${sequenceStr}`;
}

export function parseRecordId(recordId: string): {
  initials: string;
  groupCode: string;
  date: string;
  time: string;
  sequence: string;
} | null {
  const parts = recordId.split('-');
  if (parts.length !== 7) {
    return null;
  }

  return {
    initials: parts[0],
    groupCode: parts[1],
    date: `${parts[2]}-${parts[3]}-${parts[4]}`,
    time: parts[5],
    sequence: parts[6],
  };
}
